import {
    LABEL_FIELD_OPTIONS,
    LabelDataItem,
    LabelFieldKey,
    ProductLike,
    ProductSettings,
    SAMPLE_PREVIEW_PRODUCT,
} from "../components/settings/productSettings.module";
import { ProductDataProps, ProductListVariant } from "../components/products/productList/productModule";

const optionByKey = new Map(LABEL_FIELD_OPTIONS.map((option) => [option.key, option]));

/**
 * Returns only the selected label fields, in the configured print order.
 * Fields whose product value is empty are dropped so the printer never receives
 * a blank line. The barcode and its human readable code are added by the print
 * layer itself - they are always printed and are not part of this selection.
 */
export const buildLabelData = (product: ProductLike, settings: ProductSettings): LabelDataItem[] =>
    settings.label.fields.reduce<LabelDataItem[]>((items, key) => {
        const option = optionByKey.get(key);
        if (!option) {
            return items;
        }
        const value = option.getValue(product).trim();
        if (value) {
            items.push({ key: option.key, label: option.label, value });
        }
        return items;
    }, []);

/** A single printable label: one variant that actually has a SKU. */
export interface LabelRow extends ProductLike {
    productId: number;
    variantId: number;
    sku: string;
}

export interface CollectedLabelRows {
    rows: LabelRow[];
    /** Names of selected products that produced no label at all. */
    skippedProductNames: string[];
}

const hasSku = (variant: ProductListVariant) => Boolean(variant?.sku && String(variant.sku).trim());

/**
 * Turns the selected product list rows into printable labels.
 * The SKU lives on the variant, so a product yields one label per variant that
 * has one; a product whose variants all lack a SKU is reported as skipped.
 */
export const collectLabelRows = (
    products: ProductDataProps[],
    selectedIds: number[],
): CollectedLabelRows => {
    const selected = new Set(selectedIds);
    const rows: LabelRow[] = [];
    const skippedProductNames: string[] = [];

    products.forEach((product) => {
        if (!selected.has(product?.id)) {
            return;
        }

        const printable = (product?.variants ?? []).filter(hasSku);
        if (printable.length === 0) {
            skippedProductNames.push(product?.name ?? `#${product?.id}`);
            return;
        }

        printable.forEach((variant) => {
            rows.push({
                productId: product.id,
                variantId: variant.id,
                sku: String(variant.sku).trim(),
                productName: product?.name ?? "",
                // A single-variant product repeats the product name; drop it so the
                // label does not print the same text twice.
                variantName: variant?.variantName === product?.name ? "" : variant?.variantName ?? "",
                sellingPrice: variant?.price ?? null,
                brandName: product?.brandName ?? "",
                categoryName: product?.categoryName ?? "",
                weight: variant?.weightCount ?? null,
                weightUnit: variant?.weightUnit ?? "",
            });
        });
    });

    return { rows, skippedProductNames };
};

/** Stable identity for a printable row, used to key the quantity inputs. */
export const labelRowKey = (row: Pick<LabelRow, "productId" | "variantId">) =>
    `${row.productId}-${row.variantId}`;

export const LABEL_QUANTITY_MIN = 1;
export const LABEL_QUANTITY_MAX = 999;

/**
 * Parses a quantity input. Returns null when the text is not a whole number
 * inside the allowed range, so the caller can show an inline error and block
 * printing rather than silently correcting the value.
 */
export const parseLabelQuantity = (text: string): number | null => {
    const trimmed = String(text ?? "").trim();
    if (!/^\d+$/.test(trimmed)) {
        return null;
    }
    const value = parseInt(trimmed, 10);
    if (value < LABEL_QUANTITY_MIN || value > LABEL_QUANTITY_MAX) {
        return null;
    }
    return value;
};

export interface LabelPrintItem {
    row: LabelRow;
    quantity: number;
}

/**
 * Expands the modal list into one entry per unit, preserving the modal order:
 * A x2, B x3 becomes [A, A, B, B, B].
 */
export const flattenLabelRows = (items: LabelPrintItem[]): LabelRow[] =>
    items.flatMap(({ row, quantity }) =>
        Array.from({ length: Math.max(0, quantity) }, () => row),
    );

/** Same ordering, but backed by sample values - used by the settings preview card. */
export const buildSampleLabelData = (fields: LabelFieldKey[]): LabelDataItem[] =>
    fields.reduce<LabelDataItem[]>((items, key) => {
        const option = optionByKey.get(key);
        if (option) {
            items.push({
                key: option.key,
                label: option.label,
                value: option.getValue(SAMPLE_PREVIEW_PRODUCT).trim() || option.sample,
            });
        }
        return items;
    }, []);
